// Gerador do certificado "Uma Gota de Equilíbrio" (Diagnóstico Saúde e Emoções)
// Layout A4 paisagem (1122x794) — modelo aprovado.
//
// IMPORTANTE: Usa apenas as fontes padrão embutidas no jsPDF (helvetica/times),
// que já suportam acentos PT-BR via WinAnsi. Tentar baixar fontes do
// fonts.gstatic.com em runtime falha por CORS (sem Access-Control-Allow-Origin),
// o que antes corrompia o VFS e impedia a geração do PDF tanto na página
// /certificado quanto na rota pública /certificado/download.
import jsPDF from 'jspdf'

// ── Helpers ──────────────────────────────────────────────────────────────────

export function slugifyNome(nome: string): string {
  return (nome || 'aluno')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export function formatarDataPt(dataIso: string): string {
  try {
    return new Date(dataIso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: 'long', year: 'numeric',
    })
  } catch {
    return ''
  }
}

// ── Geração do PDF ──────────────────────────────────────────────────────────

/**
 * Gera o PDF do certificado "Uma Gota de Equilíbrio" no formato A4 paisagem
 * (1122 x 794 px), seguindo o modelo aprovado.
 *
 * Usa fontes padrão jsPDF:
 *   - 'times' (serif, próximo de Cormorant Garamond) → nome, frases auxiliares
 *   - 'helvetica' (sans-serif, próximo de DM Sans) → títulos, labels, rodapé
 */
export function gerarCertificadoPdf(nome: string, dataIso: string): jsPDF {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [1122, 794],
    compress: true,
  })

  // ── Fundo bege claro ──
  pdf.setFillColor(0xF8, 0xF4, 0xEF)
  pdf.rect(0, 0, 1122, 794, 'F')

  // ── Bordas duplas ──
  pdf.setDrawColor(0x2D, 0x6A, 0x4F)
  pdf.setLineWidth(2)
  pdf.rect(18, 18, 1086, 758)
  pdf.setDrawColor(0xA8, 0xDF, 0xC4)
  pdf.setLineWidth(0.8)
  pdf.rect(26, 26, 1070, 742)

  // ── Linhas decorativas horizontais ──
  pdf.setDrawColor(0xA8, 0xDF, 0xC4)
  pdf.setLineWidth(0.8)
  pdf.line(200, 108, 922, 108)
  pdf.line(200, 686, 922, 686)

  // ── 1. CERTIFICADO DE CONCLUSÃO (helvetica bold, 24px, centralizado) ──
  // charSpace de 8px afeta a largura total: (nChars - 1) * charSpace.
  // getTextWidth mede sem charSpace, então compensamos manualmente.
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(24)
  pdf.setTextColor(0x2D, 0x6A, 0x4F)
  pdf.setCharSpace(0)
  const tituloSemAcento = 'CERTIFICADO DE CONCLUSAO'
  const charSpace = 8
  const tituloBaseWidth = pdf.getTextWidth(tituloSemAcento)
  const tituloTotalWidth = tituloBaseWidth + (tituloSemAcento.length - 1) * charSpace
  const tituloX = (1122 - tituloTotalWidth) / 2
  pdf.setCharSpace(charSpace)
  pdf.text('CERTIFICADO DE CONCLUSÃO', tituloX, 148)
  pdf.setCharSpace(0)

  // ── 2. Linha fina separadora ──
  pdf.setDrawColor(0xA8, 0xDF, 0xC4)
  pdf.setLineWidth(0.8)
  pdf.line(380, 162, 742, 162)

  // ── 3. "Certificamos que" (times itálico) ──
  pdf.setFont('times', 'italic')
  pdf.setFontSize(20)
  pdf.setTextColor(0x88, 0x88, 0x88)
  pdf.text('Certificamos que', 561, 210, { align: 'center' })

  // ── 4. NOME (times bold, grande, verde) ──
  pdf.setFont('times', 'bold')
  pdf.setFontSize(54)
  pdf.setTextColor(0x2D, 0x6A, 0x4F)
  const nomeFinal = (nome || 'Aluno(a)').trim()
  pdf.text(nomeFinal, 561, 290, { align: 'center' })

  // ── 5. Linha sob o nome ──
  pdf.setDrawColor(0xA8, 0xDF, 0xC4)
  pdf.setLineWidth(0.8)
  pdf.line(180, 305, 942, 305)

  // ── 6. "concluiu com êxito o curso" (times itálico) ──
  pdf.setFont('times', 'italic')
  pdf.setFontSize(20)
  pdf.setTextColor(0x88, 0x88, 0x88)
  pdf.text('concluiu com êxito o curso', 561, 340, { align: 'center' })

  // ── 7. "Uma Gota de Equilíbrio" (times bold, escuro) ──
  pdf.setFont('times', 'bold')
  pdf.setFontSize(50)
  pdf.setTextColor(0x1A, 0x1A, 0x1A)
  pdf.text('Uma Gota de Equilíbrio', 561, 408, { align: 'center' })

  // ── 8. AROMATERAPIA INTRODUTÓRIA (helvetica, letter-spacing 3px) ──
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(11)
  pdf.setTextColor(0x99, 0x99, 0x99)
  pdf.setCharSpace(3)
  pdf.text('AROMATERAPIA INTRODUTÓRIA', 561, 440, { align: 'center' })
  pdf.setCharSpace(0)

  // ── 9. DATA DE CONCLUSÃO (label) ──
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(10)
  pdf.setTextColor(0xAA, 0xAA, 0xAA)
  pdf.setCharSpace(2)
  pdf.text('DATA DE CONCLUSÃO', 561, 528, { align: 'center' })
  pdf.setCharSpace(0)

  // ── 10. DATA DINÂMICA (times bolditalic, verde) ──
  pdf.setFont('times', 'bolditalic')
  pdf.setFontSize(26)
  pdf.setTextColor(0x2D, 0x6A, 0x4F)
  const dataFmt = formatarDataPt(dataIso || new Date().toISOString())
  pdf.text(dataFmt, 561, 562, { align: 'center' })

  // ── 11. Rodapé linha 1 ──
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9.5)
  pdf.setTextColor(0xBB, 0xBB, 0xBB)
  pdf.text(
    'Emitido por Mônica Maria  •  Aromaterapeuta e Terapeuta Integrativa  •  @reacendaluz',
    561, 718, { align: 'center' },
  )

  // ── 12. Rodapé linha 2 ──
  pdf.setFontSize(8.5)
  pdf.setTextColor(0xD0, 0xD0, 0xD0)
  pdf.setCharSpace(1.5)
  pdf.text(
    'SISTEMA DESCUBRA SEUS ÓLEOS  •  DESCUBRASEUSOLEOS.COM.BR',
    561, 734, { align: 'center' },
  )
  pdf.setCharSpace(0)

  return pdf
}

/**
 * Gera o PDF e retorna em base64 (sem prefixo data:) — para envio em e-mails.
 */
export function gerarCertificadoPdfBase64(nome: string, dataIso: string): string {
  const pdf = gerarCertificadoPdf(nome, dataIso)
  const dataUri = pdf.output('datauristring')
  return dataUri.split(',')[1] || ''
}

/**
 * Gera o PDF e dispara download automático no navegador via blob.
 */
export function baixarCertificadoPdf(nome: string, dataIso: string): void {
  const pdf = gerarCertificadoPdf(nome, dataIso)
  const blob: Blob = pdf.output('blob')
  const url = URL.createObjectURL(blob)
  const filename = `certificado-${slugifyNome(nome)}-uma-gota-de-equilibrio.pdf`

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
